/**
 * Making a new card, all on one page (asked for 2026-10-17: "Step 1 name the card, select
 * their card, step 3 upload or reuse the pictures in the system, they should be able to see
 * everything in one page, or upload new, step 4 Theme, step 5 generate the card
 * automatically or manually assign pictures; once they click generate, they go to the
 * next page"):
 *
 *   1. a name;  2. a design;  3. pictures - new ones from the device, and any of the
 *   pictures already on this device's cards, copied in (each card keeps its own);
 *   4. a theme (themes.ts): the paper, a first message, and a library picture behind it;
 *   5. fill it for me - the editor's "Fill my card" runs as the card opens (?fill=1) -
 *   or place the pictures myself.
 *
 * "Create my card" makes it and opens it in the editor.
 *
 * 2026-10-23, the owner: "make the creation of the card a decision whether create single card or
 * create an event". The steps start with that choice. An event's first step is the event itself
 * (its name is the card's name) and it has one step more, the guests; "Create my event" makes the
 * event, then the card, writes the invitation in its message frame ("Dear [Name]", filled in per
 * guest) and opens it with the guest list showing (?invite=1).
 *
 * Later that day, "allow user to create multiple cards within one event": from the events board,
 * "Add a card" makes another card for an event you have (`forEvent`) - a name, the usual steps and
 * its own guests - with the same invitation in its message frame.
 *
 * A picture that cannot be used says so at once, on its tile in step 3, and is left out
 * (2026-10-19, the owner: uploading in the steps "didn't work out" - a picture the browser
 * could not open, an iPhone HEIC one say, was dropped without a word). One that still fails
 * when the card is made is named on the card's page.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type { ProjectDocument, ZoneText } from '@foldmycard/scene';
import { api, type ImageItem, type ProjectItem } from '../api.ts';
import { addGuests, blankInput, createEvent, EventFields, invitationText, joinEvent, namesFrom } from '../invite.tsx';
import type { EventDetails, LocalEvent } from '../local/events.ts';
import { lookOf, readableOn } from '../faces/look.ts';
import { libraryGroups, type LibraryItem } from '../library/index.ts';
import { newNote } from '../note-editor.tsx';
import { PaperSwatch } from '../paper.tsx';
import { navigate } from '../router.tsx';
import { t, type StringKey } from '../strings.ts';
import { DesignStory } from '../design-story.tsx';
import { THEMES, themeMessage, themeName, type ThemeId } from '../themes.ts';
import { isHeic, MIN_PICTURE_PX, PICTURE_ACCEPT, PICTURE_PROBLEM } from '../local/pictures.ts';

export interface DesignChoice {
  id: string; name: StringKey; about: StringKey; Icon: () => React.JSX.Element;
  /** Where the fold comes from, behind the (i) beside it (design-story.tsx); on screen only. */
  story?: StringKey;
  /** No longer offered for new cards; cards already made with it still open, print and fold. */
  archived?: boolean;
}

/** A picture picked from the device; `problem` once it shows it cannot be used. */
interface NewFile { key: string; file: File; url: string; problem?: string }
/** What could not be added when the card was made, for the card's page to say (editor.tsx). */
export const addProblemsKey = (projectId: string) => `fmc.addProblems.${projectId}`;

/** What is being made: one card, or an event's invitations. */
export type MakeKind = 'card' | 'event';

export function NewCard({ designs, projects, images, onCancel, start = null, forEvent = null }: {
  designs: DesignChoice[]; projects: ProjectItem[]; images: ImageItem[]; onCancel?: () => void;
  /** Already chosen (the events dashboard's "New event"), or null to ask first. */
  start?: MakeKind | null;
  /** Another card for this event, which is there already (the events board's "Add a card"). */
  forEvent?: LocalEvent | null;
}) {
  const [kind, setKind] = useState<MakeKind | null>(forEvent ? 'event' : start);
  const [ev, setEv] = useState<EventDetails>(() => blankInput());
  const [guests, setGuests] = useState('');
  const [title, setTitle] = useState('');
  const [design, setDesign] = useState(designs[0]?.id ?? 'classic');
  /** The design whose story is open (design-story.tsx). */
  const [storyOf, setStoryOf] = useState<DesignChoice | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [files, setFiles] = useState<NewFile[]>([]);
  const [theme, setTheme] = useState<ThemeId>('generic');
  const [auto, setAuto] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const nameBox = useRef<HTMLInputElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  useEffect(() => { nameBox.current?.focus(); }, [kind]);
  useEffect(() => { if (!designs.some((d) => d.id === design) && designs[0]) setDesign(designs[0].id); }, [designs, design]);
  // the previews of new pictures are let go when the page is
  const filesRef = useRef(files);
  filesRef.current = files;
  useEffect(() => () => filesRef.current.forEach((f) => URL.revokeObjectURL(f.url)), []);

  /**
   * The pictures already here, card by card, newest card first - each picture once,
   * however many cards it was copied into.
   */
  const groups = useMemo(() => {
    const shown = new Set<string>();
    return projects.map((p) => {
      const pics = images.filter((i) => i.projectId === p.id && !i.library).filter((i) => {
        const root = i.copiedFrom ?? i.id;
        if (shown.has(root)) return false;
        shown.add(root);
        return true;
      });
      return { card: p, pics };
    }).filter((g) => g.pics.length);
  }, [projects, images]);

  const toggle = (id: string) => setChosen((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const chooseAll = (ids: string[]) => setChosen((s) => {
    const all = ids.every((id) => s.has(id));
    const next = new Set(s);
    for (const id of ids) if (all) next.delete(id); else next.add(id);
    return next;
  });
  // the new tiles are made here and now, not inside the state update: React may run that later,
  // when the picker has been emptied and the FileList with it (2026-10-19: "this one is not working")
  const addFiles = (list: File[]) => {
    const added = list.map((file) => (
      { key: `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`, file, url: URL.createObjectURL(file) }));
    setFiles((f) => [...f, ...added]);
  };
  const dropFile = (key: string) => setFiles((f) => {
    const gone = f.find((x) => x.key === key);
    if (gone) URL.revokeObjectURL(gone.url);
    return f.filter((x) => x.key !== key);
  });
  const markProblem = (key: string, problem: string) => setFiles((f) => f.map((x) => (x.key === key ? { ...x, problem } : x)));
  const usable = files.filter((f) => !f.problem);
  const count = chosen.size + usable.length;

  const event = kind === 'event';
  /** The event's details are asked for: a new event, not another card for one there already. */
  const newEvent = event && !forEvent;
  const create = async (e: FormEvent) => {
    e.preventDefault();
    const name = newEvent ? ev.title.trim() : title.trim();
    if (!name) { setError(t('wizNeedName')); nameBox.current?.focus(); return; }
    setError(null);
    setBusy(t('wizCreating'));
    let pid: string | null = null;
    // the event the card invites to: its details stay on this device (local/events.ts)
    let invitation: { id: string; details: EventDetails } | null = null;
    try {
      const made = await api.createProject(name, design);
      pid = made.project.id;
      if (newEvent) {
        const details = { ...ev, title: name, startsAt: ev.startsAt || null };
        invitation = { id: (await createEvent(details, pid)).event.id, details };
      } else if (forEvent) {
        await joinEvent(forEvent.id, pid);
        invitation = { id: forEvent.id, details: forEvent };
      }
      if (invitation && namesFrom(guests).length) await addGuests(invitation.id, namesFrom(guests), pid);
      if (chosen.size) await api.copyPictures([...chosen], pid);
      // one picture that will not go in does not stop the card: the card's page names it
      const failed: string[] = [];
      for (let i = 0; i < usable.length; i++) {
        setBusy(t('wizCopying', { n: i + 1, of: usable.length }));
        await api.upload(usable[i].file, pid).catch((err) => { failed.push(`${usable[i].file.name}: ${(err as Error).message}`); });
      }
      if (failed.length) {
        try { sessionStorage.setItem(addProblemsKey(pid), JSON.stringify(failed)); } catch { /* the card still opens */ }
      }
      setBusy(t('wizCreating'));
      await applyTheme(made.project, made.version.id, theme, invitation);
      const query = [auto && count ? 'fill=1' : '', invitation ? 'invite=1' : ''].filter(Boolean).join('&');
      navigate(`/projects/${pid}${query ? `?${query}` : ''}`);
    } catch (err) {
      // the card is there even if the theme or the event could not be put on it: open it
      if (pid) return navigate(`/projects/${pid}${invitation ? '?invite=1' : ''}`);
      setError((err as Error).message || t('somethingWrong'));
      setBusy(null);
    }
  };

  if (!kind) {
    return (
      <div className="wizard" role="group" aria-labelledby="wiz-title">
        <div className="wiz-head">
          <h2 id="wiz-title">{t('chooseTitle')}</h2>
          {onCancel && <button type="button" onClick={onCancel}>{t('cancel')}</button>}
        </div>
        <div className="make-choice">
          <button type="button" className="make-option" onClick={() => setKind('card')}>
            <span className="make-icon" aria-hidden="true">💌</span>
            <strong>{t('chooseCard')}</strong>
            <span className="small muted">{t('chooseCardAbout')}</span>
          </button>
          <button type="button" className="make-option" onClick={() => setKind('event')}>
            <span className="make-icon" aria-hidden="true">🎉</span>
            <strong>{t('chooseEvent')}</strong>
            <span className="small muted">{t('chooseEventAbout')}</span>
          </button>
        </div>
      </div>
    );
  }

  // the steps are numbered as they come: an event has one more
  let step = 0;
  const num = () => <span className="wiz-num">{++step}</span>;
  return (
    <form className="wizard" onSubmit={create} aria-labelledby="wiz-title">
      <div className="wiz-head">
        <h2 id="wiz-title">{forEvent ? t('wizTitleEventCard', { title: forEvent.title }) : t(event ? 'wizTitleEvent' : 'wizTitle')}</h2>
        <span className="row">
          {start === null && !forEvent && <button type="button" className="link" onClick={() => setKind(null)} disabled={!!busy}>{t('wizChangeChoice')}</button>}
          {onCancel && <button type="button" onClick={onCancel} disabled={!!busy}>{t('cancel')}</button>}
        </span>
      </div>

      {newEvent ? (
        <fieldset className="wiz-step" disabled={!!busy}>
          <legend>{num()}{t('wizStepEvent')}</legend>
          <div className="stack invite-form">
            <EventFields value={ev} onChange={setEv} nameRef={nameBox} />
          </div>
        </fieldset>
      ) : (
        <fieldset className="wiz-step" disabled={!!busy}>
          <legend>{num()}{t(forEvent ? 'wizEventCardName' : 'wizStep1')}</legend>
          <input ref={nameBox} className="wiz-name" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)}
            placeholder={t('newCardPlaceholder')} aria-label={t(forEvent ? 'wizEventCardName' : 'wizStep1')} aria-describedby="wiz-name-hint" />
          <p className="muted small" id="wiz-name-hint">{t(forEvent ? 'wizEventCardHint' : 'wizNameHint')}</p>
        </fieldset>
      )}

      {designs.length > 1 && (
        <fieldset className="wiz-step" disabled={!!busy}>
          <legend>{num()}{t('wizStep2')}</legend>
          <div className="designs">
            {designs.map((d) => (
              // the (i) sits beside the label, not in it: a label may hold only its own control
              <div key={d.id} className="design-wrap">
                <label className={`design${design === d.id ? ' on' : ''}`}>
                  <input type="radio" name="design" value={d.id} checked={design === d.id} onChange={() => setDesign(d.id)} />
                  <d.Icon />
                  <span><strong>{t(d.name)}</strong><span className="small muted">{t(d.about)}</span></span>
                </label>
                {d.story && (
                  <button type="button" className="design-info" aria-label={t('storyOpen', { name: t(d.name) })}
                    title={t('storyOpen', { name: t(d.name) })} onClick={() => setStoryOf(d)}>i</button>
                )}
              </div>
            ))}
          </div>
        </fieldset>
      )}

      <fieldset className="wiz-step" disabled={!!busy}>
        <legend>{num()}{t('wizStep3')}
          <span className="wiz-count small">{count === 0 ? '' : count === 1 ? t('wizChosenOne') : t('wizChosen', { n: count })}</span>
        </legend>
        <input ref={picker} type="file" accept={PICTURE_ACCEPT} multiple hidden
          onChange={(e) => { const picked = [...(e.target.files ?? [])]; e.target.value = ''; if (picked.length) addFiles(picked); }} />
        <ul className="wiz-pics">
          <li>
            <button type="button" className="wiz-add" onClick={() => picker.current?.click()}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
              <span>{t('wizAddNew')}</span>
            </button>
          </li>
          {files.map((f) => (
            <li key={f.key} className={`wiz-pic ${f.problem ? 'bad' : 'on'}`} title={f.problem}>
              {/* the browser's own try at showing it is the test: one it cannot show, it cannot use */}
              <img src={f.url} alt={f.file.name}
                onError={() => markProblem(f.key, isHeic(f.file) ? PICTURE_PROBLEM.heic : PICTURE_PROBLEM.unreadable)}
                onLoad={(e) => {
                  const im = e.currentTarget;
                  if (im.naturalWidth < MIN_PICTURE_PX || im.naturalHeight < MIN_PICTURE_PX) markProblem(f.key, PICTURE_PROBLEM.tooSmall);
                }} />
              <span className="wiz-new">{f.problem ? t('wizCannotUse') : t('wizNew')}</span>
              <button type="button" className="icon wiz-drop" aria-label={`${t('wizRemove')} ${f.file.name}`} title={t('wizRemove')}
                onClick={() => dropFile(f.key)}>×</button>
            </li>
          ))}
        </ul>
        {files.filter((f) => f.problem).map((f) => (
          <p key={f.key} className="error small" role="alert"><strong>{f.file.name}</strong>: {f.problem}</p>
        ))}
        {groups.length > 0 ? (
          <>
            <h3 className="wiz-sub">{t('wizReuse')}</h3>
            <p className="muted small">{t('wizReuseHint')}</p>
            {groups.map(({ card, pics }) => (
              <div key={card.id} className="wiz-group">
                <div className="row spread">
                  <h4>{card.title}</h4>
                  <button type="button" className="link" onClick={() => chooseAll(pics.map((p) => p.id))}>
                    {t('wizSelectAll', { n: pics.length })}
                  </button>
                </div>
                <ul className="wiz-pics">
                  {pics.map((p) => (
                    <li key={p.id} className={`wiz-pic${chosen.has(p.id) ? ' on' : ''}`}>
                      <button type="button" aria-pressed={chosen.has(p.id)} onClick={() => toggle(p.id)} title={p.name || undefined}>
                        <img src={p.urls.thumb} alt={p.name || ''} loading="lazy" />
                        <span className="wiz-tick" aria-hidden="true">✓</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </>
        ) : <p className="muted small">{t('wizReuseNone')}</p>}
        <p className="muted small">{count === 0 ? t('wizChosenNone') : t('wizMoreLater')}</p>
      </fieldset>

      <fieldset className="wiz-step" disabled={!!busy}>
        <legend>{num()}{t('wizStep4')}</legend>
        <div className="themes" role="radiogroup" aria-label={t('wizStep4')}>
          {THEMES.map((th) => (
            <label key={th.id} className={`theme${theme === th.id ? ' on' : ''}`}>
              <input type="radio" name="theme" value={th.id} checked={theme === th.id} onChange={() => setTheme(th.id)} />
              <span className="theme-paper"><PaperSwatch paper={th.paper ?? { pattern: 'plain', color: '#ffffff' }} /></span>
              <strong>{t(themeName(th.id))}</strong>
              <span className={`theme-msg font-${th.note.font}`}>{t(themeMessage(th.id))}</span>
            </label>
          ))}
        </div>
        <p className="muted small">{t('wizThemeHint')}</p>
      </fieldset>

      {event && (
        <fieldset className="wiz-step" disabled={!!busy}>
          <legend>{num()}{t('wizStepGuests')}</legend>
          <textarea rows={5} value={guests} onChange={(e) => setGuests(e.target.value)} placeholder={t('inviteGuestsHint')}
            aria-label={t('wizStepGuests')} aria-describedby="wiz-guests-hint" className="wiz-guests" />
          <p className="muted small" id="wiz-guests-hint">{t('wizGuestsHint')}</p>
        </fieldset>
      )}

      <fieldset className="wiz-step" disabled={!!busy}>
        <legend>{num()}{t('wizStep5')}</legend>
        <div className="fill-choice">
          <label className={`choice${auto ? ' on' : ''}`}>
            <input type="radio" name="fill" checked={auto} onChange={() => setAuto(true)} />
            <span className="choice-icon" aria-hidden="true">✨</span>
            <span><span className="choice-title"><strong>{t('wizAuto')}</strong><span className="badge-rec">{t('wizRecommended')}</span></span>
              <span className="small muted">{t('wizAutoAbout')}</span></span>
          </label>
          <label className={`choice${!auto ? ' on' : ''}`}>
            <input type="radio" name="fill" checked={!auto} onChange={() => setAuto(false)} />
            <span className="choice-icon" aria-hidden="true">✋</span>
            <span><strong>{t('wizManual')}</strong><span className="small muted">{t('wizManualAbout')}</span></span>
          </label>
        </div>
      </fieldset>

      {error && <p className="error" role="alert">{error}</p>}
      <div className="wiz-go">
        <button className="primary big-btn" disabled={!!busy}>{busy ?? t(forEvent ? 'wizCreateEventCard' : event ? 'wizCreateEvent' : 'wizCreate')}</button>
        {onCancel && <button type="button" onClick={onCancel} disabled={!!busy}>{t('cancel')}</button>}
      </div>
      {storyOf?.story && <DesignStory name={t(storyOf.name)} story={storyOf.story} onClose={() => setStoryOf(null)} />}
    </form>
  );
}

/**
 * The theme on the new card: its paper, its first message in the message frame,
 * and - behind the message - its picture from the library, the message's
 * colour chosen to read on it. Anything the library cannot give is left out.
 * For an event the message is the invitation, and the card is linked to the event.
 */
async function applyTheme(project: ProjectItem, versionId: string, id: ThemeId, invitation: { id: string; details: EventDetails } | null = null) {
  const th = THEMES.find((x) => x.id === id)!;
  const { version } = await api.version(project.id, versionId);
  const tpl = await api.template(project.templateId, project.paper, version.templateHash);
  const doc: ProjectDocument = structuredClone(version.document!);
  if (th.paper) doc.paper = th.paper;
  // the message frame: a design with several frames for words (the fortune teller) names one 'message'
  const message = tpl.zones.find((z) => z.kind === 'message') ?? tpl.zones.find((z) => z.defaultContent === 'text');
  if (message) {
    // the invitation is several lines: it starts at the normal size and shrinks to fit the frame
    let note: ZoneText = invitation
      ? { ...newNote(), content: invitationText(invitation.details), font: th.note.font, color: th.note.color }
      : { ...newNote(), content: t(themeMessage(id)), font: th.note.font, color: th.note.color, sizeMm: 11 };
    let image: ProjectDocument['zones'][string]['image'] = null;
    const picture = await libraryPicture(th.library).catch(() => null);
    if (picture) {
      const img = await api.addFromLibrary(picture, project.id).catch(() => null);
      if (img) {
        image = { source: 'upload', id: img.id };
        note = readableOn(note, await lookOf(img.id, img.urls.thumb), th.note.color);
      }
    }
    doc.zones[message.number] = { image, text: note };
    if (invitation) doc.invitation = { eventId: invitation.id, zone: message.number };
  }
  await api.saveVersion(project.id, versionId, doc, version.revision);
}

async function libraryPicture(want: { name: string; group: string }): Promise<LibraryItem | null> {
  const groups = libraryGroups((await api.library()).items);
  const all = groups.flatMap((g) => g.items.map((item) => ({ item, group: g.name })));
  const lower = (s: string) => s.toLowerCase();
  return all.find((x) => lower(x.item.name) === lower(want.name))?.item
    ?? all.find((x) => lower(x.group) === lower(want.group))?.item ?? null;
}

